import { LOCALE_COOKIE_NAME } from '@/config';
import { type Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Locale, type Maybe } from '@/types';
import { setDayjsLocale } from '@/utils/dateTime';
import { getImportMetaEnvProp } from '@/utils/esmHelpers';
import { getBaseLanguage, setDocumentLanguageAttribute } from '@/utils/i18n';

/**
 * Variable contenant la locale de référence (singleton) tout au long de l'exécution de l'application:
 * La variable est évaluée puis assignée au tout de début de l'application (App.vue),
 * puis est mise à jour au besoin, un changement de celle-ci déclenchant une série
 * d'événements pour mettre à jour la valeur partout où il le faut (DOM, librairies, etc.)
 **/
export const currentLanguage = ref<Locale>(getImportMetaEnvProp('VITE_APP_I18N_LOCALE') as Locale);

export default function useLocale(): Ref<Locale> {
  const { locale } = useI18n();

  /**
   * Actions entreprises lorsqu'un changement de langue est effectué.
   */
  function handleLanguageChange(newValue: Locale, oldValue: Maybe<Locale>): void {
    if (newValue === oldValue) {
      return;
    }
    locale.value = newValue; // vue-i18n
    document.cookie = `${LOCALE_COOKIE_NAME}=${newValue};Max-Age=31536000;Path=/;SameSite=Lax;Secure`;
    setDayjsLocale(newValue); // librairie day.js
    setDocumentLanguageAttribute(newValue); // HTML document
  }

  currentLanguage.value = getBaseLanguage();
  watch(currentLanguage, handleLanguageChange, { immediate: true });

  return currentLanguage;
}
